/**
 * Dosage Bag Selector Component
 * Allows selection of Kryton dosage by bag quantity instead of percentage
 */

import React from 'react'
import styles from './DosageBagSelector.module.css'

export interface DosageOption {
  bags: number
  label: string
  description?: string
}

interface DosageBagSelectorProps {
  value: number // bag quantity
  onChange: (bags: number) => void
  unitSystem: 'imperial' | 'metric'
  disabled?: boolean
  className?: string
}

// Dosage configurations as per requirements
const DOSAGE_OPTIONS = {
  imperial: [
    { bags: 1, label: 'Minimum', description: 'Light duty applications' },
    { bags: 2, label: 'Standard', description: 'Standard applications' },
    { bags: 3, label: 'Maximum', description: 'Heavy duty applications' }
  ],
  metric: [
    { bags: 1, label: 'Minimum', description: 'Light duty applications' },
    { bags: 2, label: 'Medium', description: 'Medium duty applications' },
    { bags: 3, label: 'Standard', description: 'Standard applications' },
    { bags: 4, label: 'Maximum', description: 'Heavy duty applications' }
  ]
}

const DosageBagSelector: React.FC<DosageBagSelectorProps> = ({
  value,
  onChange,
  unitSystem,
  disabled = false,
  className = ''
}) => {
  const options = DOSAGE_OPTIONS[unitSystem]
  
  const handleOptionSelect = (bags: number) => {
    if (!disabled) {
      onChange(bags)
    }
  }

  const getDisplayText = () => {
    if (unitSystem === 'imperial') {
      // Requirements 1.1: "Minimum: 1 bag, Standard: 2 bags, Maximum: 3 bags"
      return "Minimum: 1 bag, Standard: 2 bags, Maximum: 3 bags"
    } else {
      // Requirements 1.2: "Minimum: 1 bag, Medium: 2 bags, Standard: 3 bags, Maximum: 4 bags"
      return "Minimum: 1 bag, Medium: 2 bags, Standard: 3 bags, Maximum: 4 bags"
    }
  }

  const getPrimaryAbrasionSource = () => {
    if (unitSystem === 'imperial') {
      // Imperial system: 1-3 bags
      if (value === 1) {
        return {
          title: 'Light Duty Applications',
          description: 'Foot traffic, light wheeled equipment, occasional use of forklift or pallet jack'
        }
      } else if (value === 2) {
        return {
          title: 'Standard Applications',
          description: 'Constant traffic from forklifts and pallet jacks. Exposure to flowing water/erosion'
        }
      } else if (value >= 3) {
        return {
          title: 'Heavy Duty Applications',
          description: 'Heavy equipment, often with foreign particle abrasion'
        }
      }
    } else {
      // Metric system: 1-4 bags
      if (value === 1) {
        return {
          title: 'Light Duty Applications',
          description: 'Foot traffic, light wheeled equipment, occasional use of forklift or pallet jack'
        }
      } else if (value === 2) {
        return {
          title: 'Medium Duty Applications',
          description: 'Regular traffic from forklift or pallet jack'
        }
      } else if (value === 3) {
        return {
          title: 'Standard Applications',
          description: 'Constant traffic from forklifts and pallet jacks. Exposure to flowing water/erosion'
        }
      } else if (value >= 4) {
        return {
          title: 'Heavy Duty Applications',
          description: 'Heavy equipment, often with foreign particle abrasion'
        }
      }
    }
    
    // Fallback for any edge cases
    return {
      title: 'Standard Applications',
      description: 'Constant traffic from forklifts and pallet jacks. Exposure to flowing water/erosion'
    }
  }

  const selectedOption = options.find(option => option.bags === value)

  return (
    <div className={`${styles.container} ${className}`}>
      <div className={styles.header}>
        <label className={styles.label}>
          Dosage Rate
          <br />
          <small className={styles.subtitle}>Select bag quantity per cubic yard</small>
        </label>
        <div className={styles.valueDisplay}>
          <span className={styles.bagCount}>{value} bag{value !== 1 ? 's' : ''}</span>
          <span className={styles.label}>{selectedOption?.label || 'Custom'}</span>
        </div>
      </div>

      <div className={styles.optionsContainer}>
        {options.map((option) => (
          <button
            key={option.bags}
            type="button"
            onClick={() => handleOptionSelect(option.bags)}
            disabled={disabled}
            className={`${styles.option} ${value === option.bags ? styles.selected : ''}`}
            aria-pressed={value === option.bags}
          >
            <div className={styles.optionContent}>
              <div className={styles.optionHeader}>
                <span className={styles.bagNumber}>{option.bags}</span>
                <span className={styles.optionLabel}>{option.label}</span>
              </div>
              {option.description && (
                <div className={styles.optionDescription}>
                  {option.description}
                </div>
              )}
            </div>
          </button>
        ))}
      </div>

      <div className={styles.description}>
        <div className={styles.abrasionSource}>
          <h4 className={styles.abrasionTitle}>Primary Abrasion Source</h4>
          <div className={styles.abrasionContent}>
            <strong className={styles.applicationType}>{getPrimaryAbrasionSource().title}</strong>
            <p className={styles.applicationDescription}>{getPrimaryAbrasionSource().description}</p>
          </div>
        </div>
        
        <p className={styles.availableOptions}>
          Available options: {getDisplayText()}
        </p>
      </div>
    </div>
  )
}

export default DosageBagSelector